import React, { useState, useEffect } from 'react';
import { fmtDateTime } from '@shared/formatDate';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
} from '@mui/material';
import {
  MonetizationOn as EarnIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { earnCreditsService, EarnTransaction } from '../services/earnCreditsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const EarnCredits: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [wallUrl, setWallUrl] = useState<string>('');
  const [wallLoading, setWallLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch providers
  const {
    data: providers,
    isLoading: providersLoading,
    error: providersError,
  } = useQuery({
    queryKey: ['earnProviders'],
    queryFn: () => earnCreditsService.getProviders(),
    staleTime: 300000, // 5 minutes
  });

  // Fetch summary
  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ['earnSummary'],
    queryFn: () => earnCreditsService.getSummary(),
    staleTime: 30000,
  });

  // Fetch history
  const {
    data: historyData,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ['earnHistory', page, rowsPerPage],
    queryFn: () => earnCreditsService.getHistory(rowsPerPage, page * rowsPerPage),
    staleTime: 30000,
  });

  // Auto-select first provider
  useEffect(() => {
    if (providers && providers.length > 0 && !selectedProvider) {
      setSelectedProvider(providers[0].providerCode);
    }
  }, [providers, selectedProvider]);

  // Load wall URL when provider changes
  useEffect(() => {
    if (selectedProvider) {
      setWallLoading(true);
      earnCreditsService
        .getWallUrl(selectedProvider)
        .then((result) => {
          setWallUrl(result.url);
          setWallLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load wall URL:', err);
          setWallUrl('');
          setWallLoading(false);
        });
    }
  }, [selectedProvider]);

  const getStatusChip = (status: string) => {
    switch (status.toUpperCase()) {
      case 'CREDITED':
        return <Chip label="Credited" color="success" size="small" />;
      case 'PENDING':
        return <Chip label="Pending" color="warning" size="small" />;
      case 'REVERSED':
        return <Chip label="Reversed" color="error" size="small" />;
      case 'REJECTED':
        return <Chip label="Rejected" color="error" size="small" variant="outlined" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return fmtDateTime(dateStr);
  };

  const handleRefresh = () => {
    refetchSummary();
    refetchHistory();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EarnIcon color="primary" />
          Earn Credits
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
        <Card elevation={2}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Lifetime Earnings
            </Typography>
            <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
              {summaryLoading ? (
                <Skeleton width={80} />
              ) : (
                `$${(summary?.lifetimeEarnings ?? 0).toFixed(2)}`
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary?.totalCompletedOffers ?? 0} offers completed
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              This Month
            </Typography>
            <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
              {summaryLoading ? (
                <Skeleton width={80} />
              ) : (
                `$${(summary?.thisMonthEarnings ?? 0).toFixed(2)}`
              )}
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Pending
            </Typography>
            <Typography variant="h6" color="warning.main" sx={{ fontWeight: 'bold' }}>
              {summaryLoading ? (
                <Skeleton width={80} />
              ) : (
                `$${(summary?.pendingAmount ?? 0).toFixed(2)}`
              )}
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={2}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">
              Reversed
            </Typography>
            <Typography variant="h6" color="error.main" sx={{ fontWeight: 'bold' }}>
              {summaryLoading ? (
                <Skeleton width={80} />
              ) : (
                `$${(summary?.reversedAmount ?? 0).toFixed(2)}`
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary?.totalReversedOffers ?? 0} reversed
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs */}
      <Paper elevation={2}>
        <Tabs
          value={tabIndex}
          onChange={(_, newVal) => setTabIndex(newVal)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<EarnIcon />} iconPosition="start" label="Earn" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="My Earnings" />
        </Tabs>

        {/* Tab 1: Earn (Offerwall) */}
        <TabPanel value={tabIndex} index={0}>
          <Box sx={{ px: 2, pb: 2 }}>
            {providersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : providersError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load providers. Please try again later.
              </Alert>
            ) : !providers || providers.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Earn Credits Coming Soon!
                </Typography>
                <Typography variant="body2">
                  Complete offers, surveys, and other activities to earn real 2Way Credits
                  that you can spend on anything in the marketplace. Providers are being
                  configured and will be available shortly.
                </Typography>
              </Alert>
            ) : (
              <>
                {providers.length > 1 && (
                  <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
                    <InputLabel>Select Provider</InputLabel>
                    <Select
                      value={selectedProvider}
                      onChange={(e) => setSelectedProvider(e.target.value)}
                      label="Select Provider"
                    >
                      {providers.map((p) => (
                        <MenuItem key={p.providerCode} value={p.providerCode}>
                          {p.providerName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Complete offers below to earn real 2Way Credits. Credits are added to your
                    account balance immediately upon completion. Some offers may be reversed if
                    the advertiser declines the conversion.
                  </Typography>
                </Alert>

                {wallLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : wallUrl ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: '600px',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <iframe
                      src={wallUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                      title={`${selectedProvider} Offers`}
                      sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation"
                    />
                  </Box>
                ) : (
                  <Alert severity="warning">
                    Unable to load the offer wall. Please try selecting a different provider
                    or refreshing the page.
                  </Alert>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        {/* Tab 2: My Earnings (History) */}
        <TabPanel value={tabIndex} index={1}>
          <Box sx={{ px: 2, pb: 2 }}>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !historyData || historyData.transactions.length === 0 ? (
              <Alert severity="info">
                <Typography variant="body2">
                  You haven't completed any offers yet. Visit the "Earn" tab to start earning credits!
                </Typography>
              </Alert>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Provider</TableCell>
                        <TableCell>Offer</TableCell>
                        <TableCell align="right">Earned</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyData.transactions.map((tx: EarnTransaction) => (
                        <TableRow key={tx.transactionId} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDate(tx.completedDate || tx.createdDate)}
                          </TableCell>
                          <TableCell>{tx.providerName}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {tx.offerTitle || 'Offer'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  tx.status.toUpperCase() === 'REVERSED'
                                    ? 'error.main'
                                    : 'success.main',
                                fontWeight: 'bold',
                              }}
                            >
                              {tx.status.toUpperCase() === 'REVERSED' ? '-' : '+'}$
                              {tx.amountEarned.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{getStatusChip(tx.status)}</TableCell>
                          <TableCell>
                            {tx.status.toUpperCase() === 'REVERSED' && tx.reversalReason && (
                              <Typography variant="caption" color="error">
                                {tx.reversalReason}
                              </Typography>
                            )}
                            {tx.status.toUpperCase() === 'CREDITED' && tx.creditedDate && (
                              <Typography variant="caption" color="text.secondary">
                                Credited {formatDate(tx.creditedDate)}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={historyData.totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default EarnCredits;
